// src/components/applicant/PersonalDetailsCard.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import apiClient from "../../services/apiClient";
import Modal from "react-modal";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes } from "@fortawesome/free-solid-svg-icons";
import Snackbar from "../common/Snackbar";
import PersonalDetailsEditPopup from "./PersonalDetailsEditPopup";
import { faPen } from "@fortawesome/free-solid-svg-icons";
import { useRefresh } from "../common/RefreshContext";
import analytics from "../../utils/analytics";

const PERSONAL_API = '/applicant-personal';
const RESUME_API = '/applicant-pdf';

const PersonalDetailsCard = ({ applicantId, onLoaded, showContent, onChange }) => {
  const [bd, setBd] = useState({});
  const [open, setOpen] = useState(false);
  const [snackbars, setSnackbars] = useState([]);
  const [resumeAvailable, setResumeAvailable] = useState(false);
  const fileInputRef = useRef(null);
  const { refreshKey } = useRefresh();
  const currentUser = JSON.parse(localStorage.getItem("user"));

  const addSnackbar = (snackbar) => setSnackbars((p) => [...p, snackbar]);
  const handleCloseSnackbar = (index) =>
    setSnackbars((p) => p.filter((_, i) => i !== index));

  const fetchBD = async () => {
    try {
      const { data } = await apiClient.get(`${PERSONAL_API}/${applicantId}/getApplicantPersonalDetails`);
      const result = data || {};
      setBd(result);
      return result;
    } catch (e) {
      console.error("Failed to load personal details:", e?.response || e);
      setBd({});
      return {};
    }
  };

  const probeResume = async () => {
    try {
      const res = await apiClient.get(`${RESUME_API}/getresume/${applicantId}`, {
        responseType: "blob",
        validateStatus: (s) => (s >= 200 && s < 300) || s === 404,
      });
      setResumeAvailable(res.status !== 404 && res.data?.size > 0);
    } catch {
      setResumeAvailable(false);
    }
  };

  useEffect(() => {
    const loadPersonalDetails = async () => {
      if (!applicantId) return;
      await Promise.all([
        fetchBD(),
        probeResume()
      ]);

      onLoaded?.();
    };

    loadPersonalDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applicantId]);
  useEffect(() => {
    if (!applicantId) return;

    fetchBD();
    probeResume();
  }, [refreshKey]);
  const fullName = useMemo(() => (bd?.name || "").trim(), [bd]);
  const phone = bd?.phone || "";
  const email = bd?.email || "";
  const dob = bd?.dateOfBirth || "";
  const pincode = bd?.pincode || "";
  const address = bd?.address || "";
  const gender = bd?.gender || "";
  const languages = Array.isArray(bd?.knownLanguages) ? bd.knownLanguages.join(", ") : "";
  const onResumeClick = () => fileInputRef.current?.click();
  // resume upload handlers
  const onFilePicked = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const MAX_SIZE = 5 * 1024 * 1024; // 5MB
    if (file.size > MAX_SIZE) {
      addSnackbar({
        message: "File size should be less than 5 MB",
        type: "error",
      });
      e.target.value = "";
      return;
    }
    const jwt = localStorage.getItem("jwtToken");
    const form = new FormData();
    form.append("resume", file);
    const candidates = [
      `${RESUME_API}/${applicantId}/upload`,
    ];
    let success = false, lastErr;
    for (const url of candidates) {
      try {
        await apiClient.post(url, form, {
          headers: {
            "Content-Type": "multipart/form-data"
          },
          timeout: 30000
        });
        success = true;
        addSnackbar({ message: "Resume uploaded successfully!", type: "success" });
        await probeResume?.();
        break;
      } catch (err) {
        lastErr = err;
      }
    }
    if (!success) {
      const status = lastErr?.response?.status;
      const url = lastErr?.config?.url;
      // 🚨 IMPORTANT: when request is canceled 
      if (!lastErr?.response) {
        addSnackbar({
          message: "Upload failed. File size should be less than 5 MB",
          type: "error",
        });
        e.target.value = "";
        return;
      }
      const data = lastErr.response.data;
      let serverText = "";
      if (typeof data === "string") {
        serverText = data;
      } else if (typeof data === "object" && data !== null) {
        serverText = data.message || data.error || "";
      }
      addSnackbar({
        message: serverText
          ? serverText
          : `Upload failed (${status || "network error"}) at ${url}`,
        type: "error",
      });
    }
    e.target.value = "";

  };


  const onViewResume = async () => {
    try {
      const response = await apiClient.get(`${RESUME_API}/getresume/${applicantId}`, {
        responseType: "blob",
      });
      const blob = new Blob([response.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank", "noopener,noreferrer");
    } catch (error) {
      console.error("Error fetching resume:", error);
      addSnackbar({ message: "Unable to open resume.", type: "error" });
    }
  };

  if (!showContent) {
    return (
      <div className="col-lg-12 col-md-12 common_style">
        <div className="card-base soft-shadow">
          <div className="card-title-row">
            <div className="skeleton" style={{ width: 180, height: 20 }} />
            <div className="skeleton" style={{ width: 60, height: 20 }} />
          </div>

          <div className="skeleton" style={{ width: 320, height: 15, marginTop: 10 }} />

          <div className="pd-grid" style={{ marginTop: 20 }}>
            {[...Array(8)].map((_, i) => (
              <div
                key={i}
                className="skeleton"
                style={{ height: 40, borderRadius: 8 }}
              />
            ))}
          </div>

          <div
            className="skeleton"
            style={{ height: 80, marginTop: 20, borderRadius: 10 }}
          />
        </div>
      </div>
    );
  }
  return (
    <>
      <div className="col-lg-12 col-md-12 common_style">
        <div className="card-base soft-shadow">
          <div className="card-title-row">
            <h4 className="card-title">
              Personal details <span className="req">*</span>
            </h4>
            <button
              type="button"
              className="portfolio-edit-btn"
              onClick={() => setOpen(true)}>
              Edit <FontAwesomeIcon icon={faPen} style={{ marginRight: "6px" }} />
            </button>
          </div>

          <p className="card-subtitle">This information is important for employers to know you better</p>

          <div className="pd-grid">
            <input
              className="pd-input common_style"
              readOnly
              placeholder="Enter full name"
              value={fullName || ""}
            />
            <div className="pd-select-wrap">
              <div className="profile-dropdown common_style">
                <div className="pd-selected">
                  {gender || "choose gender"}
                </div>
                <span className="pd-caret">▾</span>
              </div>
            </div>
            <input
              className="pd-input common_style"
              readOnly
              placeholder="Enter email"
              value={email || ""}
            />

            <input
              className="pd-input common_style"
              readOnly
              placeholder="Enter phone number"
              value={phone || ""}
            />
            <div className="pd-input with-icon">
              <input
                className="pd-input raw common_style"
                readOnly
                placeholder="Date of birth"
                value={dob || ""}
              />
              <span className="pd-icon common_style" aria-hidden>
                📅
              </span>
            </div>
            <input
              className="pd-input common_style"
              readOnly
              placeholder="PIN code"
              value={pincode || ""}
            />

            <input
              className="pd-input span-2 common_style"
              readOnly
              placeholder="Permanent address"
              value={address || ""}
            />
            <div className="pd-input with-add">
              <input
                className="pd-input raw common_style"
                readOnly
                placeholder="Known language(s)"
                value={languages || ""}
              />
            </div>
          </div>

          {/* Resume upload / view */}
          <div className="resume-drop common_style" tabIndex={0}>
            <div className="resume-drop-inner">
              <span className="resume-pill" onClick={() => {
                analytics.track("RESUME UPLOAD", currentUser?.id);
                onResumeClick();
              }}>
                <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M12 16V8M8 12h8" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" />
                </svg>
                Resume upload
              </span>
              <div className="resume-note">Supported formats: pdf</div>
              {resumeAvailable && (
                <div style={{ marginTop: 8 }}>
                  <button
                    type="button"
                    className="btn-primary"
                    style={{ background: "#475569" }}
                    onClick={(e) => {
                      e.stopPropagation();
                      onViewResume();
                    }}
                  >
                    View current resume
                  </button>
                </div>
              )}
            </div>
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,.doc,.docx,.rtf"
            style={{ display: "none" }}
            onChange={onFilePicked}
          />
        </div>
      </div>

      {/* Edit modal */}
      <Modal
        isOpen={open}
        onRequestClose={() => setOpen(false)}
        contentLabel="Edit Personal Details"
        className="modal-content2"
        overlayClassName="modal-overlay"
        ariaHideApp={false}
      >
        <div style={{ position: "absolute", top: 10, right: 20 }}>
          <FontAwesomeIcon
            icon={faTimes}
            onClick={() => setOpen(false)}
            style={{ cursor: "pointer", color: "#333" }}
          />
        </div>

        <PersonalDetailsEditPopup
          applicantId={applicantId}
          initial={{
            fullName: fullName || "",
            gender: gender || "",
            email: email || "",
            phone: phone || "",
            dateOfBirth: dob || "",
            pincode: pincode || "",
            address: address || "",
            knownLanguages: Array.isArray(bd?.knownLanguages) ? bd.knownLanguages : [],
          }}
          onSuccess={async () => {
            await fetchBD();
            setOpen(false);
            addSnackbar({ message: "Personal details updated successfully!", type: "success" });
          }}
          onError={(msg) =>
            addSnackbar({ message: msg || "Failed to update personal details", type: "error" })
          }
        />
      </Modal>

      {snackbars.map((snackbar, index) => (
        <Snackbar
          key={index}
          index={index}
          message={snackbar.message}
          type={snackbar.type}
          onClose={handleCloseSnackbar}
        />
      ))}
    </>
  );
};

export default PersonalDetailsCard;
